import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { formatFeatureLabel, getFeatureDefinition } from '../../featureLabels';


// Feature Card Modal Component
export const FeatureCardModal = ({ isOpen, onClose, model, channelValues, channelValuesLoading, zScores, zScoresLoading, showZScores = true }) => {
    const modalRef = useRef(null);
    const plotAreaRef = useRef(null); // Ref for the plot area
    const [clickedChannelModal, setClickedChannelModal] = useState(null);

    // For z-scores, we'll use a fixed range of -3 to +3 standard deviations
    const zScoreRange = 3;
    // For raw values, use a display range of mean ± 3*std (used in normalizeRelativeToCenteredMean)
    const displayStdDevMultiple = 3;

    const formatLabelForCard = (value) => {
        const absValue = Math.abs(value);
        if (absValue >= 1000) {
            return value.toFixed(0);
        } else if (absValue >= 100) {
            return value.toFixed(1);
        } else {
            return value.toFixed(2);
        }
    };

    const handleModalDotClick = (e, channel) => {
        e.stopPropagation();
        if (!plotAreaRef.current) return;

        const plotAreaWidth = plotAreaRef.current.offsetWidth;
        const dotHorizontalPercent = showZScores
            ? normalizeZScore(channel.z_score || 0) // Make sure channel.z_score is passed
            : normalizeRelativeToCenteredMean(channel.value);
        const dotPixelX = (dotHorizontalPercent / 100) * plotAreaWidth; // Center of the dot

        const estimatedPopupWidth = 220; // From CSS min-width 200px + padding 10px*2
        const offset = 6; // px offset from the dot

        let finalLeftPixel = dotPixelX + offset; // Default: popup to the right of the dot

        // Check if popup overflows to the right
        if (dotPixelX + offset + estimatedPopupWidth > plotAreaWidth) {
            finalLeftPixel = dotPixelX - estimatedPopupWidth - offset; // Move popup to the left of the dot
        }
        // Ensure popup doesn't go off the left edge either
        if (finalLeftPixel < 0) {
            finalLeftPixel = offset; // Place it with a small offset from the left edge
        }

        setClickedChannelModal({
            data: channel,
            style: {
                top: `calc(50% + ${12 / 2 + offset}px)`, // Half dot height (12px/2) + offset
                left: `${finalLeftPixel}px`,
            }
        });
    };

    const handleModalPlotAreaClick = (e) => {
        // Only close if the click is directly on the plot area, not on the popup itself or a dot
        if (e.target === e.currentTarget) {
            setClickedChannelModal(null);
        }
    };

    // Handle click outside to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.body.classList.add('modal-open');
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.body.classList.remove('modal-open');
        };
    }, [isOpen, onClose]);

    // Close on escape key
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    // For z-scores, normalize between -3 and +3
    const normalizeZScore = (zScoreInput) => {
        // Clamp the zScoreInput to the range [-zScoreRange, zScoreRange]
        const clampedZScore = Math.max(-zScoreRange, Math.min(zScoreRange, zScoreInput));
        // Convert clamped z-score to percentage position (0-100%)
        return ((clampedZScore + zScoreRange) / (2 * zScoreRange)) * 100;
    };

    // For raw values, normalize relative to mean ± 3*std
    const normalizeRelativeToCenteredMean = (value) => {
        if (model.std === 0) {
            return 50;
        }
        const plotDisplayMin = model.mean - (displayStdDevMultiple * model.std);
        const plotDisplayMax = model.mean + (displayStdDevMultiple * model.std);
        const plotDisplayRange = plotDisplayMax - plotDisplayMin;
        if (plotDisplayRange === 0) {
            return 50;
        }
        const normalizedValue = ((value - plotDisplayMin) / plotDisplayRange) * 100;
        return Math.max(0, Math.min(100, normalizedValue));
    };

    // Fixed positions for standard z-score markers (used for modal plot lines and labels)
    const zeroPos = 50; // 0 is always at 50%
    const minusOnePos = normalizeZScore(-1);
    const plusOnePos = normalizeZScore(1);
    const minusTwoPos = normalizeZScore(-2);
    const plusTwoPos = normalizeZScore(2);

    // Fixed positions for raw value markers for the x-axis labels
    const meanPos = 50; // Mean is always at 50%
    const meanMinusStdPos = normalizeRelativeToCenteredMean(model.mean - model.std);
    const meanPlusStdPos = normalizeRelativeToCenteredMean(model.mean + model.std);
    const meanMinus2StdPos = normalizeRelativeToCenteredMean(model.mean - 2 * model.std);
    const meanPlus2StdPos = normalizeRelativeToCenteredMean(model.mean + 2 * model.std);

    return createPortal(
        <div className="feature-card-modal">
            <div className="modal-overlay" onClick={onClose}></div>
            <div ref={modalRef} className="modal-content" onClick={handleModalPlotAreaClick}>
                <button onClick={onClose} className="modal-close-button">
                    <X size={24} />
                </button>

                <div className="modal-title-container">
                    <div className="modal-title-line">
                        <span className="modal-title-label">Brain Region:</span>
                        <span className="modal-header-region">{model.region_name}</span>
                    </div>
                    <div className="modal-title-line">
                        <span className="modal-title-label">Feature:</span>
                        <span className="modal-feature-value" title={getFeatureDefinition(model.feature_name).description}>
                            {formatFeatureLabel(model.feature_name)}
                            <small>{model.feature_name}</small>
                        </span>
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '180px' }}>
                        <div className="normative-stats">
                            <div className="stat">
                                <span className="stat-label">Mean:</span>
                                <span className="stat-value">
                                    {model.mean.toFixed(2)}
                                </span>
                            </div>
                            <div className="stat">
                                <span className="stat-label">Std:</span>
                                <span className="stat-value">
                                    {model.std.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div style={{ flex: 1, minWidth: '180px' }}>
                        <div className="normative-stats">
                            <div className="stat">
                                <span className="stat-label">Min:</span>
                                <span className="stat-value">
                                    {model.min.toFixed(2)}
                                </span>
                            </div>
                            <div className="stat">
                                <span className="stat-label">Max:</span>
                                <span className="stat-value">
                                    {model.max.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div style={{ flex: 1, minWidth: '180px' }}>
                        <div className="normative-stats">
                            <div className="stat">
                                <span className="stat-label">Channels:</span>
                                <span className="stat-value">
                                    {model.sample_size}
                                </span>
                            </div>
                            <div className="stat">
                                <span className="stat-label">Display:</span>
                                <span className="stat-value z-score-value">
                                    {showZScores ? "Z-Scores" : "Raw Values"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Distribution plot for modal */}
                <div className="normative-plot modal-distribution-plot" onClick={handleModalPlotAreaClick}>
                    <div className="distribution-plot-area">
                        <div
                            ref={plotAreaRef}
                            className="distribution-gaussian-curve modal-distribution-plot-curve"
                            style={{
                                height: '150px',
                                background: 'linear-gradient(to right, transparent, rgba(100, 140, 255, 0.5), transparent)',
                                borderRadius: '50%/100%',
                                position: 'relative'
                            }}
                        >
                            {/* Center line - Z-score = 0 or Raw Value Mean */}
                            <div
                                className="distribution-mean-line"
                                style={{
                                    position: 'absolute',
                                    left: showZScores ? `${zeroPos}%` : `${meanPos}%`,
                                    top: '0',
                                    height: '100%',
                                    width: '3px', // Thickest for mean
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)'
                                }}
                            ></div>

                            {/* Standard deviation lines for Z-Score plot or Raw Value plot */}
                            {/* ±1 std */}
                            <div
                                className="distribution-std-minus"
                                style={{
                                    position: 'absolute',
                                    left: showZScores ? `${minusOnePos}%` : `${meanMinusStdPos}%`,
                                    top: '0',
                                    height: '100%',
                                    width: '2px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.5)'
                                }}
                            ></div>
                            <div
                                className="distribution-std-plus"
                                style={{
                                    position: 'absolute',
                                    left: showZScores ? `${plusOnePos}%` : `${meanPlusStdPos}%`,
                                    top: '0',
                                    height: '100%',
                                    width: '2px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.5)'
                                }}
                            ></div>

                            {/* ±2 std */}
                            <div
                                className="distribution-std-minus-2"
                                style={{
                                    position: 'absolute',
                                    left: showZScores ? `${minusTwoPos}%` : `${meanMinus2StdPos}%`,
                                    top: '0',
                                    height: '100%',
                                    width: '1px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.4)'
                                }}
                            ></div>
                            <div
                                className="distribution-std-plus-2"
                                style={{
                                    position: 'absolute',
                                    left: showZScores ? `${plusTwoPos}%` : `${meanPlus2StdPos}%`,
                                    top: '0',
                                    height: '100%',
                                    width: '1px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.4)'
                                }}
                            ></div>

                            {/* Plot individual channel z-scores as dots */}
                            {showZScores && !zScoresLoading && zScores && zScores.map((score, index) => {
                                const position = normalizeZScore(score.z_score);
                                const currentZScore = Math.abs(score.z_score);
                                const colorIntensity = Math.min(1, currentZScore / 3);
                                const dotColor = `rgba(${255}, ${220 - 160 * colorIntensity}, ${0}, 0.8)`;

                                return (
                                    <div
                                        key={`${score.patient_id}-${score.channel}-${index}`}
                                        className="channel-value-dot"
                                        style={{
                                            position: 'absolute',
                                            left: `${position}%`,
                                            top: '50%',
                                            width: '12px',
                                            height: '12px',
                                            borderRadius: '50%',
                                            backgroundColor: dotColor,
                                            zIndex: 2,
                                            transform: 'translate(-50%, -50%)',
                                            boxShadow: '0 0 6px rgba(0, 0, 0, 0.3)',
                                            cursor: 'pointer',
                                            animation: 'fadeIn 0.2s ease-in-out',
                                            opacity: 1
                                        }}
                                        onClick={(e) => handleModalDotClick(e, {
                                            ...score,
                                            value: score.z_score // Use z_score as the value for display in popup
                                        })}
                                    />
                                );
                            })}

                            {/* If showing raw values explicitly */}
                            {!showZScores && !channelValuesLoading && channelValues && channelValues.map((channel, index) => {
                                const position = normalizeRelativeToCenteredMean(channel.value);
                                const zScoreForColor = model.std > 0 ? Math.abs((channel.value - model.mean) / model.std) : 0;
                                const colorIntensity = Math.min(1, zScoreForColor / 3);
                                const dotColor = `rgba(${255}, ${220 - 160 * colorIntensity}, ${0}, 0.8)`;

                                return (
                                    <div
                                        key={`${channel.patient_id}-${channel.channel}-${index}`}
                                        className="channel-value-dot"
                                        style={{
                                            position: 'absolute',
                                            left: `${position}%`,
                                            top: '50%',
                                            width: '12px',
                                            height: '12px',
                                            borderRadius: '50%',
                                            backgroundColor: dotColor,
                                            zIndex: 2,
                                            transform: 'translate(-50%, -50%)',
                                            boxShadow: '0 0 6px rgba(0, 0, 0, 0.3)',
                                            cursor: 'pointer',
                                            animation: 'fadeIn 0.2s ease-in-out',
                                            opacity: 1
                                        }}
                                        onClick={(e) => handleModalDotClick(e, channel)}
                                    />
                                );
                            })}

                            {/* Click-triggered Popup for Modal Plot */}
                            {clickedChannelModal && (
                                <div
                                    className="channel-click-popup modal-channel-popup"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                        position: 'absolute',
                                        top: clickedChannelModal.style.top,
                                        left: clickedChannelModal.style.left,
                                        zIndex: 10010
                                    }}
                                >
                                    <button
                                        className="channel-popup-close-button"
                                        onClick={() => setClickedChannelModal(null)}
                                    >
                                        &times;
                                    </button>
                                    <div>Patient: {clickedChannelModal.data.patient_id}</div>
                                    <div>Channel: {clickedChannelModal.data.channel}</div>
                                    {clickedChannelModal.data.z_score !== undefined && (
                                        <div>Z-Score: {typeof clickedChannelModal.data.z_score === 'number'
                                            ? clickedChannelModal.data.z_score.toFixed(2)
                                            : clickedChannelModal.data.z_score}
                                        </div>
                                    )}
                                    {((showZScores && clickedChannelModal.data.value !== undefined && clickedChannelModal.data.value !== clickedChannelModal.data.z_score) ||
                                        (!showZScores && clickedChannelModal.data.value !== undefined)) && (
                                            <div>Value: {clickedChannelModal.data.value.toFixed(2)}</div>
                                        )}
                                </div>
                            )}
                        </div>
                        {/* X-axis ticks for Modal plot*/}
                        <div className="distribution-axis" style={{ position: 'relative', height: '20px', marginTop: '8px', fontSize: '14px' }}>
                            {showZScores ? (
                                <>
                                    <span style={{ position: 'absolute', left: `${minusTwoPos}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>-2</span>
                                    <span style={{ position: 'absolute', left: `${minusOnePos}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>-1</span>
                                    <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>0</span>
                                    <span style={{ position: 'absolute', left: `${plusOnePos}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>+1</span>
                                    <span style={{ position: 'absolute', left: `${plusTwoPos}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>+2</span>
                                </>
                            ) : (
                                // Raw value scale for Modal
                                <>
                                    <span style={{ position: 'absolute', left: `${meanMinus2StdPos}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>{formatLabelForCard(model.mean - 2 * model.std)}</span>
                                    <span style={{ position: 'absolute', left: `${meanMinusStdPos}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>{formatLabelForCard(model.mean - model.std)}</span>
                                    <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>{formatLabelForCard(model.mean)}</span>
                                    <span style={{ position: 'absolute', left: `${meanPlusStdPos}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>{formatLabelForCard(model.mean + model.std)}</span>
                                    <span style={{ position: 'absolute', left: `${meanPlus2StdPos}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>{formatLabelForCard(model.mean + 2 * model.std)}</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};
